import axios from 'axios'
import { v4 } from 'uuid'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  callPresignedUrl,
  completeMultipartUpload,
  createPresignedUrl,
  initiateMultipartUpload,
} from 'api/storage'
import constants from '../../../../constants'
import { fileStatus } from '../Media/media'
import { getMediaDimensions } from 'utils/helpers'
import { getPlacementTypeFromDimensions } from './carouselPlacement'

const PART_SIZE = 20 * 1000 * 1000 //20MB

export const validateMediaFile = (file) => {
  if (!file) return false

  const isImage = ['image/jpeg', 'image/png'].includes(file.type)
  const isVideo = ['video/mp4', 'video/quicktime'].includes(file.type)
  if (!isImage && !isVideo) return false

  const fileSizeMB = file.size / 1000 / 1000
  if (isImage) return fileSizeMB <= constants.MAX_IMG_UPLOAD_SIZE
  return fileSizeMB <= constants.MAX_VIDEO_UPLOAD_SIZE
}

const uploadSingleMediaFile = async (file, options = {}) => {
  const { onProgress, onCancelTokenSource } = options
  const cancelTokenSource = axios.CancelToken.source()
  onCancelTokenSource?.(cancelTokenSource)
  const expectedTotalParts = Math.ceil(file.size / PART_SIZE)

  const { content: uploadInitData, error: initError } = await initiateMultipartUpload(
    {
      object_name: file.name,
      content_type: file.type,
    },
    { cancelToken: cancelTokenSource.token, upload_type: 'media' },
  )
  if (initError) throw new Error('Failed to initiate upload')

  const uploadId = uploadInitData.upload_id
  const objectKey = uploadInitData.object_key

  const { content: presignedUrls, error: presignedError } = await createPresignedUrl(
    {
      upload_id: uploadId,
      object_key: objectKey,
      start_part_number: 1,
      total_parts: expectedTotalParts,
      total_uploaded_parts: 0,
    },
    { cancelToken: cancelTokenSource.token, upload_type: 'media' },
  )
  if (presignedError) throw new Error('Failed to create presigned urls')

  const parts = []
  let totalUploadedParts = 0
  for (let index = 0; index < expectedTotalParts; index++) {
    const offset = index * PART_SIZE
    const part = file.slice(offset, offset + PART_SIZE)
    const presignedUrl = presignedUrls[index]

    const { content: partResult, error: uploadPartError } = await callPresignedUrl(
      presignedUrl,
      part,
      { cancelToken: cancelTokenSource.token },
      (partProgress) => {
        if (!onProgress) return
        const totalProgress = Math.round(
          (1 / expectedTotalParts) * (totalUploadedParts * 100 + partProgress),
        )
        onProgress(totalProgress)
      },
    )

    if (uploadPartError) throw new Error('Failed to upload file part')

    parts.push({
      part_number: index + 1,
      etag: partResult.etag,
    })
    totalUploadedParts += 1
  }

  const { content: completeData, error: completeError } = await completeMultipartUpload(
    {
      upload_id: uploadId,
      object_key: objectKey,
      parts,
    },
    { cancelToken: cancelTokenSource.token, upload_type: 'media' },
  )
  if (completeError) throw new Error('Failed to complete upload')

  const dimensions = await getMediaDimensions(file)

  return {
    id: v4(),
    filename: file.name,
    path: file.path,
    name: file.name,
    type: file.type,
    size: file.size,
    height: dimensions.height,
    width: dimensions.width,
    placement_type: getPlacementTypeFromDimensions(dimensions),
    placement_group_id: null,
    group_index: null,
    source: 'media',
    error: null,
    partsData: parts,
    expectedTotalParts,
    totalUploadedParts: expectedTotalParts,
    uploadId,
    objectKey,
    cancelTokenSource,
    progress: 100,
    preview: URL.createObjectURL(file),
    url: completeData.location,
    status: fileStatus.success,
  }
}

export const useCarouselUploadStore = () => {
  const [isMediaLoadingByCard, setIsMediaLoadingByCard] = useState({})
  const [mediaProgressByCard, setMediaProgressByCard] = useState({})
  const [uploadErrorByCard, setUploadErrorByCard] = useState({})
  const cancelTokenSourceByCardRef = useRef({})
  const uploadRequestIdByCardRef = useRef({})

  useEffect(() => {
    return () => {
      Object.values(cancelTokenSourceByCardRef.current).forEach((source) => {
        if (source) {
          source.cancel('Upload cancelled on component unmount')
        }
      })
      cancelTokenSourceByCardRef.current = {}
    }
  }, [])

  const clearUploadError = useCallback((cardId) => {
    if (!cardId) return
    setUploadErrorByCard((prev) => ({ ...prev, [cardId]: '' }))
  }, [])

  // Cards whose upload has finished but haven't been merged into
  // carousel_cards yet — consumed by CarouselAd's own effect (see
  // consumeCompletedUpload below), which reports them upward via
  // onCarouselCardsChange with whatever cards/form are current *at the time
  // the effect runs*. Deliberately not reported via a callback invoked
  // directly from this async function: that callback would be captured once,
  // when the upload starts, and held stale for the upload's whole duration —
  // any edit to another field in the meantime (e.g. body) would get
  // overwritten with the value from that stale moment once the callback
  // finally fires. Mirrors MediaUpload.service.jsx's own pattern (that
  // upload path only ever dispatches to its own reducer; a separate effect,
  // re-evaluated on every relevant render, is what reports to the parent).
  const [completedUploadByCard, setCompletedUploadByCard] = useState({})

  const consumeCompletedUpload = useCallback((cardId) => {
    setCompletedUploadByCard((prev) => {
      if (!(cardId in prev)) return prev
      const next = { ...prev }
      delete next[cardId]
      return next
    })
  }, [])

  const uploadCardMedia = useCallback(async (cardId, file) => {
    if (!cardId || !file) return

    if (cancelTokenSourceByCardRef.current[cardId]) {
      cancelTokenSourceByCardRef.current[cardId].cancel('Upload replaced with new file')
      cancelTokenSourceByCardRef.current[cardId] = null
    }

    const uploadRequestId = (uploadRequestIdByCardRef.current[cardId] || 0) + 1
    uploadRequestIdByCardRef.current[cardId] = uploadRequestId

    try {
      clearUploadError(cardId)
      setIsMediaLoadingByCard((prev) => ({ ...prev, [cardId]: true }))
      setMediaProgressByCard((prev) => ({ ...prev, [cardId]: 0 }))

      const uploadedMedia = await uploadSingleMediaFile(file, {
        onProgress: (progress) => {
          setMediaProgressByCard((prev) => ({ ...prev, [cardId]: progress }))
        },
        onCancelTokenSource: (source) => {
          cancelTokenSourceByCardRef.current[cardId] = source
        },
      })

      if (uploadRequestId !== uploadRequestIdByCardRef.current[cardId]) {
        return
      }

      setCompletedUploadByCard((prev) => ({ ...prev, [cardId]: uploadedMedia }))
      setMediaProgressByCard((prev) => ({ ...prev, [cardId]: 100 }))
      clearUploadError(cardId)
    } catch (error) {
      if (axios.isCancel(error)) return
      console.error('InfoCard media upload failed', error)
      setMediaProgressByCard((prev) => ({ ...prev, [cardId]: 0 }))
    } finally {
      if (uploadRequestIdByCardRef.current[cardId] === uploadRequestId) {
        setIsMediaLoadingByCard((prev) => ({ ...prev, [cardId]: false }))
        cancelTokenSourceByCardRef.current[cardId] = null
      }
    }
  }, [clearUploadError])

  const cancelUpload = useCallback((cardId) => {
    if (!cardId) return
    uploadRequestIdByCardRef.current[cardId] = (uploadRequestIdByCardRef.current[cardId] || 0) + 1
    if (cancelTokenSourceByCardRef.current[cardId]) {
      cancelTokenSourceByCardRef.current[cardId].cancel('Upload cancelled by user')
      cancelTokenSourceByCardRef.current[cardId] = null
    }

    setIsMediaLoadingByCard((prev) => ({ ...prev, [cardId]: false }))
    setMediaProgressByCard((prev) => ({ ...prev, [cardId]: 0 }))
  }, [])

  const getCardUploadState = useCallback((cardId) => ({
    isMediaLoading: Boolean(isMediaLoadingByCard[cardId]),
    mediaProgress: mediaProgressByCard[cardId] ?? 0,
    uploadError: uploadErrorByCard[cardId] || '',
  }), [isMediaLoadingByCard, mediaProgressByCard, uploadErrorByCard])

  return {
    uploadCardMedia,
    cancelUpload,
    clearUploadError,
    setUploadErrorByCard,
    getCardUploadState,
    completedUploadByCard,
    consumeCompletedUpload,
  }
}
